import { formatMoney } from 'common/src/other/currencyFormat';
import React,{ useState, useEffect, useRef } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import {
  Grid,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import AlertDialog from '../components/AlertDialog';
import AlertDialogTwoButton from '../components/AlertDialogTwoButton';
import styles from '../styles/landingPage.js';
import { useNavigate } from 'react-router-dom';
import {colors} from '../components/Theme/WebTheme';
import { FONT_FAMILY, MAIN_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import {
  C2_FONT,
  c2ModalBoxSx,
  c2TextFieldSx,
  c2PrimaryBtnSx,
  c2SecondaryBtnSx,
  c2SuccessBtnSx,
} from '../components/conecta2ModalStyles';

const useStyles = makeStyles(theme => ({
  ...styles,
  modal: {
    display: 'flex',
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 400,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  inputRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
  textField: {
    "& label.Mui-focused": {
      color: MAIN_COLOR,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: MAIN_COLOR,
      },
    },
  },
  selectField: {
    color: "black",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: MAIN_COLOR,
    },
  },
}));

const icons = {
  'mercadopago':require('../assets/payment-icons/mercadopago-logo.png').default,
  'openpay':require('../assets/payment-icons/credicard.png').default,
  'pse':require('../assets/payment-icons/pse.png').default,
  'epayco':require('../assets/payment-icons/epayco-logo.jpg').default
}

/** Billetera: COP$ 23.500 */
function formatWalletAmount(value) {
  return formatMoney(value || 0, { country: 'Colombia', code: 'COP', decimal: 0 });
}

function typeBadgeLabel(type, t) {
  if (type === 'credited') return t('credited');
  if (type === 'withdraw') return t('withdrawn');
  if (type === 'debited') return t('debited');
  return t(type);
}

function typeBadgeColor(type) {
  if (type === 'credited') return '#22C55E';
  if (type === 'withdraw') return '#F59E0B';
  return '#EF4444';
}

const UserWallet = (props) => {
  const {
    withdrawBalance,
  } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const providers = useSelector(state => state.paymentmethods.providers);
  const [profile,setProfile] = useState();
  const [data,setData] = useState([]);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [commonAlertTwoButton, setCommonAlertTwoButton] = useState({ open: false, msg: '' });
  const rootRef = useRef(null);
  const [amount,setAmount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalInfo,setModalInfo] = useState({
    mOpen: false,
    mType: null
  });
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const columns =  [
      {
        title: t('requestDate'),
        field: 'date',
        defaultSort:'desc',
        render: rowData =>
          rowData.date
            ? moment(rowData.date).locale('es').format('D [de] MMM [de] YYYY HH:mm')
            : null,
      },
      {
        title: t('amount'),
        field: 'amount',
        editable: 'never',
        render: (rowData) => (
          <Typography
            component="span"
            sx={{
              fontFamily: "'Outfit', 'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#1A0F3B',
            }}
          >
            {formatWalletAmount(rowData.amount || 0)}
          </Typography>
        ),
      },
     { title: t('transaction_id'), field: 'transaction_id', render: rowData => rowData.transaction_id ? rowData.transaction_id : rowData.txRef, },
     {
      title: t('type'),
      field: 'type',
      render: (rowData) => (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            backgroundColor: typeBadgeColor(rowData.type),
            color: '#FBFAFE',
            px: 2,
            py: 0.6,
            borderRadius: '999px',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.04em',
            fontFamily: "'Outfit', 'Poppins', sans-serif",
            minWidth: 110,
            textAlign: 'center',
          }}
        >
          {typeBadgeLabel(rowData.type, t)}
        </Box>
      ),
    },
  ];

  useEffect(()=>{
    if(providers){
      setSelectedProvider(providers[0]);
    }
  },[providers]);

  useEffect(()=>{
    if(auth.profile){
        setProfile(auth.profile);
        let wdata = auth.walletHistory;
        var wallHis = [];
        for(let key in wdata){
            wdata[key].walletKey = key
            if(wdata[key].type.includes("Credit") || wdata[key].type.includes("credit")){
              wdata[key].type = 'credited'
            }else if(wdata[key].type.includes("Withdraw") || wdata[key].type.includes("withdraw")){
              wdata[key].type = 'withdraw'
            }else{
              wdata[key].type = 'debited'
            }
            wallHis.push(wdata[key])
        }
        if(wallHis.length>0){
          setData(wallHis.reverse());
        }else{
          setData([]);
        }

    } else{
        setProfile(null);
    }
  },[auth.profile,auth.walletHistory]);

  const doRecharge = (e) => {
    e.preventDefault();
    if(!(profile.mobile && profile.mobile.length > 6 && profile.email && profile.firstName)){
      setCommonAlertTwoButton({
        open: true,
        msg: t('profile_incomplete', {
          defaultValue: 'Completa tu perfil (nombre, correo y teléfono) para continuar.',
        }),
      })
     } else{
      if (providers) {
        setModalInfo({
          mOpen: true,
          mType: 'add'
        });
      } else {
        setCommonAlert({
          open: true,
          msg: t('provider_not_found', {
            defaultValue: 'No se encontraron proveedores de pago.',
          }),
        })
      }
    }
  }

  const doWithdraw = (e) => {
    e.preventDefault();
    if(!(profile.mobile && profile.mobile.length > 6 && profile.email && profile.firstName)){
      setCommonAlertTwoButton({
        open: true,
        msg: t('profile_incomplete', {
          defaultValue: 'Completa tu perfil (nombre, correo y teléfono) para continuar.',
        }),
      })
    }else{
        if (api.getWithdrawableBalance(profile) > 0) {
          setModalInfo({
            mOpen: true,
            mType: 'withdraw'
          });
        } else {
          setCommonAlert({
            open: true,
            msg: t('wallet_bal_low', {
              defaultValue: 'Saldo de billetera insuficiente.',
            }),
          })
        }
    }
  }

  const gotoOperation = () => {
    navigate('/profile');
    setCommonAlert({ open: false, msg: '' })
  };

  const cancelOperation = () => {
    setAmount(0);
    setModalInfo({
      mOpen: false,
      mType: null
    });
    setCommonAlert({ open: false, msg: '' })
  };

  const cancelOperationTowButton = () => {
    setAmount(0);
    setModalInfo({
      mOpen: false,
      mType: null
    });
    setCommonAlertTwoButton({ open: false, msg: '' })
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if(amount <= 0){
      setCommonAlert({ open: true, msg: t('valid_amount')});
    } else {
       if(modalInfo.mType === 'add'){
        setModalInfo({
          ...modalInfo,
          mOpen: false
        });
        setPaymentModalStatus(true);
       }else{
        if (api.getWithdrawableBalance(profile) < amount) {
          setCommonAlert({
            open: true,
            msg: api.getWalletPromoBalance(profile) > 0
              ? `Puedes retirar hasta ${formatWalletAmount(api.getWithdrawableBalance(profile))}. El saldo de campaña solo sirve para tus viajes.`
              : t('valid_amount'),
          });
        }else{
          dispatch(withdrawBalance(profile,amount));
          cancelOperation();
         }
        }
    }
  }

  const createOrderId = () =>{
    const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const reference = [...Array(4)].map(_ => c[~~(Math.random()*c.length)]).join('');
    return "wallet-" + auth.profile.uid + "-" + reference
  }

  const handlePaymentModalClose = (e) => {
    setTimeout(()=>{
      setPaymentModalStatus(false);
    },1500);
  }
  const inputStyle = { fontFamily: FONT_FAMILY, };

  const walletTableOptions = {
    ...TableStyle(),
    headerStyle: {
      ...TableStyle().headerStyle,
      backgroundColor: '#EBE7FB',
      color: '#1A0F3B',
      border: '1px solid #D7CFEB',
      fontFamily: "'Outfit', 'Poppins', sans-serif",
      fontWeight: 700,
      fontSize: '0.8em',
      padding: '12px 10px',
    },
    cellStyle: {
      ...TableStyle().cellStyle,
      border: '1px solid #EDE8F7',
      fontFamily: "'Outfit', 'Poppins', sans-serif",
      fontSize: '0.88em',
      color: '#221448',
    },
    searchFieldAlignment: 'right',
    toolbarButtonAlignment: 'right',
    searchFieldStyle: {
      borderRadius: 10,
      border: '1px solid #D7CFEB',
      padding: '2px 8px',
      fontFamily: "'Outfit', 'Poppins', sans-serif",
      backgroundColor: '#FBFAFE',
    },
    showTitle: false,
  };
 
  return (
    <Box sx={{ px: { xs: 0.5, sm: 1 }, pb: 2 }}>
      <Typography
        sx={{
          fontFamily: "'Outfit', 'Poppins', sans-serif",
          fontWeight: 800,
          fontSize: { xs: '1.6rem', sm: '2rem' },
          color: '#1A0F3B',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          mb: 2,
          textAlign: isRTL === 'rtl' ? 'right' : 'left',
        }}
      >
        {t('my_wallet_title') || 'BILLETERA'}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '18px',
          background: 'linear-gradient(105deg, #6411ED 0%, #7628FF 48%, #5B0FD9 100%)',
          boxShadow: '0 12px 32px rgba(100,17,237,0.28)',
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          mb: 2.5,
          minHeight: { xs: 140, sm: 150 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: { xs: -20, sm: 24 },
            top: '50%',
            transform: 'translateY(-50%) rotate(-12deg)',
            width: 120,
            height: 76,
            borderRadius: '12px',
            border: '2px solid rgba(251,250,254,0.18)',
            opacity: 0.55,
            display: { xs: 'none', md: 'block' },
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: { xs: -10, sm: 56 },
            top: '42%',
            transform: 'translateY(-50%) rotate(8deg)',
            width: 120,
            height: 76,
            borderRadius: '12px',
            border: '2px solid rgba(251,250,254,0.12)',
            opacity: 0.4,
            display: { xs: 'none', md: 'block' },
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '14px',
                backgroundColor: 'rgba(251,250,254,0.16)',
                border: '1px solid rgba(251,250,254,0.28)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AccountBalanceWalletOutlinedIcon sx={{ color: '#FBFAFE', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Outfit', 'Poppins', sans-serif",
                  fontSize: '0.85rem',
                  color: 'rgba(251,250,254,0.88)',
                  mb: 0.3,
                }}
              >
                {t('Balance') || 'Balance'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Outfit', 'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: '1.75rem', sm: '2.1rem' },
                  color: '#FBFAFE',
                  lineHeight: 1.1,
                  letterSpacing: '0.02em',
                }}
              >
                {formatWalletAmount(profile?.walletBalance || 0)}
              </Typography>
              {api.getWalletPromoBalance(profile) > 0 ? (
                <Typography
                  sx={{
                    fontFamily: "'Outfit', 'Poppins', sans-serif",
                    fontSize: '0.78rem',
                    color: 'rgba(251,250,254,0.8)',
                    mt: 0.5,
                  }}
                >
                  {formatWalletAmount(api.getWalletPromoBalance(profile))} de campaña (solo para viajes, no retirable)
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: '1px',
              alignSelf: 'stretch',
              minHeight: 64,
              backgroundColor: 'rgba(251,250,254,0.35)',
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0, maxWidth: 420 }}>
            <Typography
              sx={{
                fontFamily: "'Outfit', 'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                color: '#FBFAFE',
                mb: 0.5,
              }}
            >
              Tu saldo disponible
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Outfit', 'Poppins', sans-serif",
                fontSize: { xs: '0.85rem', sm: '0.92rem' },
                color: 'rgba(251,250,254,0.85)',
                lineHeight: 1.45,
              }}
            >
              Úsalo para tus viajes y servicios dentro de Conecta2.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: '#FBFAFE',
          borderRadius: '16px',
          border: '1px solid #D7CFEB',
          boxShadow: '0 8px 24px rgba(26,15,59,0.06)',
          overflow: 'hidden',
          p: { xs: 1, sm: 1.5 },
        }}
      >
      <ThemeProvider theme={theme}>
      <MaterialTable
        title=""
        columns={columns}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '12px',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
        data={data}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        components={{
          Toolbar: (toolbarProps) => (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
                px: 0.5,
                mb: 0.5,
                '& .MuiToolbar-root': {
                  minHeight: 52,
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                  flex: '1 1 auto',
                  justifyContent: 'flex-end',
                },
                '& .MuiIconButton-root': {
                  color: '#6411ED',
                  border: '1px solid #D7CFEB',
                  borderRadius: '10px',
                  width: 40,
                  height: 40,
                  backgroundColor: '#FBFAFE',
                  '&:hover': {
                    backgroundColor: '#EBE7FB',
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Outfit', 'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  color: '#1A0F3B',
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
              >
                {t('transaction_history_title') || 'HISTORIAL DE TRANSACCIONES'}
              </Typography>
              <MTableToolbar {...toolbarProps} />
            </Box>
          ),
        }}
        options={{
          exportButton: true,
          rowStyle: (rowData) => ({
            backgroundColor:
              selectedRow === rowData.tableData.id ? colors.THIRDCOLOR : colors.WHITE,
          }),
          ...walletTableOptions,
        }}
        localization={localization(t)}
      />
      </ThemeProvider>
      </Box>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={paymentModalStatus}
        onClose={handlePaymentModalClose}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Box sx={{ ...c2ModalBoxSx, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
        {providers && selectedProvider && modalInfo.mType === 'add'  && amount>0?
          <form action={selectedProvider.link} method="POST">
            <input type='hidden' name='order_id' value={createOrderId()}/>
            <input type='hidden' name='amount' value={amount}/>
            <input type='hidden' name='currency' value={settings.code}/>
            <input type='hidden' name='product_name' value={t('add_money')}/>
            <input type='hidden' name='first_name' value={profile.firstName}/>
            <input type='hidden' name='last_name' value={profile.lastName}/>
            <input type='hidden' name='quantity' value={1}/>
            <input type='hidden' name='cust_id' value={auth.profile.uid}/>
            <input type='hidden' name='mobile_no' value={profile.mobile}/>
            <input type='hidden' name='email' value={profile.email}/>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '1.15rem', color: '#1A0F3B', mb: 2 }}>
              {t('payment', { defaultValue: 'Pago' })}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <FormLabel sx={{ fontFamily: C2_FONT, color: '#766293', mb: 1 }}>{t('payment')}</FormLabel>
              <Select
                fullWidth
                id="selectedProviderIndex"
                name="selectedProviderIndex"
                value={selectedProviderIndex}
                label={t('payment')}
                onChange={(e)=>{
                    setSelectedProviderIndex(parseInt(e.target.value));
                    setSelectedProvider(providers[parseInt(e.target.value)]);
                }}
                sx={{
                  borderRadius: '12px',
                  fontFamily: C2_FONT,
                  textAlign: isRTL === 'rtl' ? 'right' : 'left',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D7CFEB' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6411ED' },
                }}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                {providers.map((provider,index) =>
                   <MenuItem key={provider.name} value={index} style={{ width:'100%', justifyContent:'center', paddingLeft:10}}><img style={{height:24,margin:7}} src={icons[provider.name]} alt={provider.name}/> </MenuItem>
                )}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={handlePaymentModalClose} variant="outlined" sx={c2SecondaryBtnSx}>
                {t('cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button variant="contained" type="submit" onClick={handlePaymentModalClose} sx={c2SuccessBtnSx}>
                {t('paynow_button', { defaultValue: 'Pagar ahora' })}
              </Button>
            </Box>
          </form>
          :null}
        </Box>
      </Modal>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={modalInfo.mOpen}
        onClose={cancelOperation}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Box sx={{ ...c2ModalBoxSx, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '1.15rem', color: '#1A0F3B', mb: 1 }}>
                {t('amount', { defaultValue: 'Monto' })}
            </Typography>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="amount"
                label={t('amount')}
                name="amount"
                autoComplete="amount"
                sx={c2TextFieldSx}
                onChange={(e)=>{
                  try{
                    if(e.target.value === ""){
                      setAmount(0);
                    }else{
                      setAmount(parseFloat(e.target.value));
                    }
                  }catch(e){
                    setCommonAlert({ open: true, msg: t('no_details_error')});
                    setAmount(0);
                  }          
                }}
                value={amount.toString()}
                autoFocus
                className={isRTL==='rtl'?classes.inputRtl:null}
                style={{direction:isRTL==='rtl'?'rtl':'ltr' }}
              />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Button onClick={cancelOperation} variant="outlined" sx={c2SecondaryBtnSx}>
              {t('cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button onClick={handleSubmit} variant="contained" sx={c2PrimaryBtnSx}>
              {modalInfo.mType === 'add'? t('add_to_wallet'): t('withdraw')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <AlertDialog open={commonAlert.open} onClose={cancelOperation}>{commonAlert.msg}</AlertDialog>
      <AlertDialogTwoButton open={commonAlertTwoButton.open} onClose={cancelOperationTowButton} onGoto={gotoOperation}>{commonAlertTwoButton.msg}</AlertDialogTwoButton>
    </Box>
  );
}

export default UserWallet;
